import React, { useRef, useState } from 'react';
import { apiFetch } from '../../api/api';

export default function PairPrintManager({ onClose, onConnected }) {
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState(null);
  const [devices, setDevices] = useState([]);
  const [operation, setOperation] = useState('add');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const approval = useRef(null);
  async function resolve(event) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const pending = await apiFetch('/api/printmanager/pairing/preview', { method:'POST', body:{pairingCode:code} });
      const choices = await apiFetch('/api/printmanager/pairing/devices');
      approval.current = crypto.randomUUID(); setDevices(choices); setPreview(pending);
      if(pending.recovery){setOperation('replace');setTarget(pending.targetDeviceId);}
    } catch { setError('Unable to find this pairing code. Check the code, expiration, and organization permissions.'); }
    finally { setBusy(false); }
  }
  async function confirm() {
    setBusy(true); setError(''); setSubmitted(true);
    try {
      await apiFetch('/api/printmanager/pairing/approve', {method:'POST',body:{sessionId:preview.sessionId,
        pairingCode:code, approvalId:approval.current, operation, ...(operation==='replace'?{targetDeviceId:target}:{})}});
      setConnected(true); onConnected?.();
    } catch(e) {
      const reason=e?.response?.data?.error;
      setError(['OUTSTANDING_ASSIGNMENTS','UNCERTAIN_EXECUTION','OUTSTANDING_PROJECTION','OUTSTANDING_DELIVERY','DEVICE_BUSY'].includes(reason)
        ? 'Printing or delivery receipts still need attention. Let active work finish and reconnect Print Manager, then retry this confirmation. Keep historical jobs unchanged.'
        : reason==='CURRENT_OWNER_REQUIRED' ? 'Select the currently registered organization and ask its Owner or Admin to approve this local recovery.'
        : 'Confirmation was not acknowledged. Retry the same confirmation; do not start another pairing.');
    }
    finally { setBusy(false); }
  }
  return <section role="dialog" aria-modal="true" aria-label="Add Print Manager">
    <h2>Add Print Manager</h2>
    {connected ? <p>Claim accepted. PM3 verifies its authenticated heartbeat and printer discovery before enabling printing. Offline printers remain unavailable.</p> : !preview ? <form onSubmit={resolve}>
      <label>Enter pairing code <input value={code} maxLength={12} autoComplete="off" onChange={e=>setCode(e.target.value)} /></label>
      <button disabled={busy || !code.trim()} type="submit">Continue</button>
    </form> : <>
      <p>Print Manager: {preview.machineName}</p><p>Pairing expires: {new Date(preview.expiresAt).toLocaleTimeString()}</p>
      {preview.recovery&&<p>Local recovery confirmed. Only this registration's current owner can reconnect it. Printing stays inactive until local history and authenticated discovery are verified.</p>}
      <label><input type="radio" name="pair-operation" checked={operation==='add'} disabled={busy || submitted || preview.recovery} onChange={()=>setOperation('add')} />Add as New Print Manager</label>
      <label><input type="radio" name="pair-operation" checked={operation==='replace'} disabled={busy || submitted || preview.recovery} onChange={()=>setOperation('replace')} />Replace Existing Print Manager</label>
      {operation==='replace' && <><p>The existing identity and history stay. The old installation will lose access.</p>
        <label>Existing Print Manager <select value={target} disabled={busy || submitted || preview.recovery} onChange={e=>setTarget(e.target.value)}>
          <option value="">Select a Print Manager</option>{devices.map(d=><option key={d.deviceId} value={d.deviceId}>{d.deviceName || d.deviceId}{d.isActive?'':' (Registered / Inactive)'}</option>)}
        </select></label></>}
      {operation==='add' && <p>Registration is free. This Print Manager remains inactive until its authenticated heartbeat and printer discovery are verified. Offline printers remain unavailable.</p>}
      <button onClick={confirm} disabled={busy || (operation==='replace'&&!target)}>Confirm</button>
    </>}
    {error && <p role="alert">{error}</p>}
    <button onClick={onClose} disabled={busy}>{connected?'Done':'Cancel'}</button>
  </section>;
}
